/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Stack } from '@fluentui/react/lib/Stack';
import { TooltipDelay } from '@fluentui/react/lib/Tooltip';
import { Property } from 'csstype';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';

type ScoreCellProps = {
    /**
     * The utterance to be displayed.
     */
    utteranceObservable: UtteranceObservable;

    /**
     * The last training date for the app.
     */
    lastTrainingDate: Date;
};

const messages = defineMessages({
    untrainedUtteranceScoreDescription: {
        id: 'utterances.utteranceTable.untrainedUtteranceScoreDescription',
        defaultMessage: `Scores shown are from the last trained model, which does not include
        this utterance in the training data. Retrain your model to include untrained data.`
    }
});

const tableTheme = getScopedTheme('UtteranceTable');
const { getClassName } = getStylist('UtteranceTable');
const underlinedText = getClassName('underlinedText', theme => ({ borderBottom: theme.untrainedBorder }));

const NaText = 'N/A';

/**
 * Renders the score cell in the utterance table for the given utterance.
 */
export const ScoreCell = observer((props: ScoreCellProps) => {
    const { utteranceObservable, lastTrainingDate } = props;
    const { localize } = useLocalization();

    const { utteranceWithAnalytics, computed } = utteranceObservable;
    const [utterance] = computed.get();
    const isUntrainedUtterance = !lastTrainingDate || utterance.modifiedDate > lastTrainingDate;

    let content = NaText;

    if (utteranceWithAnalytics.predictionStore) {
        const intentScore = utteranceWithAnalytics.predictionStore.predictedIntents.find(
            ({ intentId }) => utteranceWithAnalytics.utteranceStore.labeledIntentId === intentId
        );

        if (intentScore) {
            content = intentScore.score === -1 ? NaText : intentScore.score.toFixed(3);
        }
    }

    return (
        <Stack verticalFill verticalAlign="center">
            <LuisTooltip
                hidden={!isUntrainedUtterance}
                delay={TooltipDelay.zero}
                content={localize(messages.untrainedUtteranceScoreDescription)}
                calloutProps={{ directionalHint: DirectionalHint.topCenter, beakWidth: 0 }}>
                <LuisText
                    className={isUntrainedUtterance ? underlinedText : ''}
                    fontWeight={(isUntrainedUtterance ? tableTheme.boldFont : tableTheme.regularFont) as Property.FontWeight}>
                    {content}
                </LuisText>
            </LuisTooltip>
        </Stack>
    );
});
